import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { platformService } from '../../services/platform';
import { LoadingSpinner, ErrorState, EmptyState } from '../../components/common';

/**
 * Platform admin panel (superadmin-only, PO direction 2026-07-20):
 * cross-tenant operator view — every organization with status, and
 * "Open" to operate inside one via the rbac superadmin bypass. Invites
 * / signup-referral offers ride this surface next (desk-integration.md
 * §9). Deliberately OUTSIDE tenant context — its own route + shell.
 */
export function PlatformPage() {
  const { user, signOut } = useAuth();
  const { enterTenant } = useTenant();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = useQuery({
    queryKey: ['platform', 'tenants', cursor],
    queryFn: () => platformService.listTenants({ limit: 50, startAfter: cursor }),
    enabled: user?.globalRole === 'superadmin',
  });

  if (user?.globalRole !== 'superadmin') {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="error-state" role="alert">
            Platform administration requires superadmin.
          </div>
        </div>
      </div>
    );
  }

  const open = (t: { id: string; name: string; status: string }) => {
    enterTenant({
      tenantId: t.id,
      name: t.name,
      status: t.status,
      role: 'account_admin',
      clientIds: null,
    });
    navigate('/');
  };

  return (
    <div className="platform-page">
      <header className="platform-header">
        <h1>Detect — Platform</h1>
        <div className="platform-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/')}
          >
            Back to app
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="platform-main">
        <h2>Organizations</h2>
        {query.isPending && <LoadingSpinner label="Loading organizations…" />}
        {query.isError && (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        )}
        {query.data && query.data.items.length === 0 && (
          <EmptyState
            title="No organizations yet"
            description="Customers appear here as they sign up."
          />
        )}
        {query.data && query.data.items.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Id</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>
                      <code>{t.id}</code>
                    </td>
                    <td>{'plan' in t ? t.plan : '—'}</td>
                    <td>{t.status}</td>
                    <td>
                      {'createdAt' in t
                        ? new Date(t.createdAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => open(t)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {query.data?.pagination?.hasMore && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setCursor(query.data.pagination?.nextCursor)}
          >
            Load more
          </button>
        )}
      </main>
    </div>
  );
}
